'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Settings, X, Trash2, Eye, EyeOff, Plus } from 'lucide-react'

// Interface for MCP Server configuration
export interface MCPServer {
  id: string
  name: string
  url: string
  type: 'sse' | 'stdio'
  enabled: boolean
  env?: Record<string, string>
  command?: string
  args?: string[]
  // OAuth Configuration
  oauth?: {
    authorizationUrl?: string
    tokenUrl?: string
    clientId?: string
    clientSecret?: string
    scopes?: string[]
    accessToken?: string
    refreshToken?: string
    expiresAt?: number
    requiresAuth: boolean
    authStatus: 'none' | 'pending' | 'authenticated' | 'error'
    authError?: string
  }
}

// LocalStorage key for MCP servers
const MCP_SERVERS_STORAGE_KEY = 'nin-terminal-mcp-servers'

// Default MCP servers - Trading Terminal focused
export const DEFAULT_MCP_SERVERS: MCPServer[] = [
  {
    id: '1',
    name: 'Filesystem MCP',
    url: '',
    type: 'stdio',
    enabled: false,
    args: ['@modelcontextprotocol/server-filesystem', '--allowed-directories', './trading_logs', './strategies', './data'],
    env: {},
    command: 'npx'
  },
  {
    id: '2',
    name: 'SQLite MCP',
    url: '',
    type: 'stdio', 
    enabled: false,
    args: ['@modelcontextprotocol/server-sqlite', '--db-path', './trading_data.db'],
    env: {},
    command: 'npx'
  },
  {
    id: '3',
    name: 'Web Search MCP',
    url: '',
    type: 'stdio',
    enabled: false,
    args: ['@modelcontextprotocol/server-brave-search'],
    env: {
      BRAVE_API_KEY: 'your-brave-api-key-here'
    },
    command: 'npx'
  },
  {
    id: '4',
    name: 'GitHub MCP',
    url: '',
    type: 'stdio',
    enabled: false,
    args: ['@modelcontextprotocol/server-github'],
    env: {
      GITHUB_PERSONAL_ACCESS_TOKEN: 'your-github-token-here'
    },
    command: 'npx'
  },
  {
    id: '5',
    name: 'Sequential Thinking',
    url: '',
    type: 'stdio',
    enabled: false,
    args: ['-y', '@modelcontextprotocol/server-sequential-thinking'],
    env: {},
    command: 'npx'
  }
]

// Recommended Trading MCP Servers for Discovery
export const RECOMMENDED_TRADING_SERVERS: Array<{
  name: string
  description: string
  url: string
  type: 'sse' | 'stdio'
  category: string
  env?: Record<string, string>
  command?: string
  args?: string[]
}> = [
  {
    name: 'Yahoo Finance MCP',
    description: 'Real-time stock quotes, financial data, and market information',
    url: '',
    type: 'stdio',
    category: 'Market Data',
    env: {},
    command: 'npx',
    args: ['@modelcontextprotocol/server-yahoofinance']
  },
  {
    name: 'PostgreSQL MCP',
    description: 'Database storage for trading data, backtesting results, and portfolio tracking',
    url: '',
    type: 'stdio',
    category: 'Database',
    env: {
      POSTGRES_CONNECTION_STRING: 'postgresql://user:password@localhost:5432/trading_db'
    },
    command: 'npx',
    args: ['@modelcontextprotocol/server-postgres']
  },
  {
    name: 'Email Notifications MCP',
    description: 'Send trading alerts and notifications via email',
    url: '',
    type: 'stdio',
    category: 'Notifications',
    env: {
      SMTP_HOST: 'smtp.gmail.com',
      SMTP_PORT: '587',
      EMAIL_USER: 'your-email@gmail.com',
      EMAIL_PASS: 'your-app-password'
    },
    command: 'npx',
    args: ['@modelcontextprotocol/server-email']
  },
  {
    name: 'Slack MCP',
    description: 'Post trading updates and alerts to Slack channels',
    url: '',
    type: 'stdio',
    category: 'Notifications',
    env: {
      SLACK_BOT_TOKEN: 'your-slack-bot-token',
      SLACK_CHANNEL: '#trading-alerts'
    },
    command: 'npx',
    args: ['@modelcontextprotocol/server-slack']
  },
  {
    name: 'Redis MCP',
    description: 'High-performance caching for real-time trading data',
    url: '',
    type: 'stdio',
    category: 'Caching',
    env: {
      REDIS_URL: 'redis://localhost:6379'
    },
    command: 'npx',
    args: ['@modelcontextprotocol/server-redis']
  }
]

// Helper functions for localStorage
export const saveMcpServersToStorage = (servers: MCPServer[]) => {
  try {
    console.log('🔍 Saving servers to storage:', servers)
    const obfuscated = btoa(JSON.stringify(servers))
    localStorage.setItem(MCP_SERVERS_STORAGE_KEY, obfuscated)
    console.log('✅ Successfully saved to localStorage')
  } catch (error) {
    console.error('Failed to save MCP servers to localStorage:', error)
  }
}

export const loadMcpServersFromStorage = (): MCPServer[] => {
  try {
    console.log('🔍 Loading servers from storage...')
    const stored = localStorage.getItem(MCP_SERVERS_STORAGE_KEY)
    if (stored) {
      let parsed
      try {
        parsed = JSON.parse(atob(stored))
      } catch {
        parsed = JSON.parse(stored)
      }
      
      console.log('🔍 Parsed servers from storage:', parsed)
      
      if (Array.isArray(parsed) && parsed.every(server => 
        server.id && server.name && server.type &&
        ((server.type === 'sse' && server.url) || (server.type === 'stdio' && (server.command || server.url)))
      )) {
        console.log('✅ Validation passed, returning stored servers')
        return parsed
      } else {
        console.log('❌ Validation failed, returning default servers')
      }
    } else {
      console.log('📭 No stored servers found, returning default servers')
    }
  } catch (error) {
    console.error('Failed to load MCP servers from localStorage:', error)
  }
  console.log('🔄 Returning default servers:', DEFAULT_MCP_SERVERS)
  return DEFAULT_MCP_SERVERS
}

// OAuth helper functions
export const detectOAuthRequirement = async (server: MCPServer): Promise<{ requiresAuth: boolean; metadata?: any }> => {
  try {
    // Try to access the server without authentication first
    const response = await fetch(server.url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    })
    
    // Check for OAuth metadata endpoints as per RFC 8414 and Aaron Parecki's article
    const wellKnownUrls = [
      `${server.url}/.well-known/oauth-authorization-server`,
      `${server.url}/.well-known/oauth-protected-resource`, // Protected Resource Metadata
      `${new URL(server.url).origin}/.well-known/oauth-authorization-server`,
      `${new URL(server.url).origin}/.well-known/oauth-protected-resource`,
    ]
    
    for (const wellKnownUrl of wellKnownUrls) {
      try {
        const metadataResponse = await fetch(wellKnownUrl)
        if (metadataResponse.ok) {
          const metadata = await metadataResponse.json()
          return {
            requiresAuth: true,
            metadata: {
              ...metadata,
              discoveryUrl: wellKnownUrl
            }
          }
        }
      } catch (error) {
        // Continue to next URL
        continue
      }
    }
    
    // Check if the response indicates authentication is required
    if (response.status === 401 || response.status === 403) {
      const wwwAuth = response.headers.get('WWW-Authenticate')
      if (wwwAuth && wwwAuth.toLowerCase().includes('bearer')) {
        return { requiresAuth: true }
      }
    }
    
    return { requiresAuth: false }
  } catch (error) {
    console.error('Error detecting OAuth requirement:', error)
    return { requiresAuth: false }
  }
}

export const initiateOAuthFlow = (server: MCPServer): string => {
  if (!server.oauth?.authorizationUrl || !server.oauth?.clientId) {
    throw new Error('OAuth configuration incomplete')
  }
  
  const state = btoa(JSON.stringify({ serverId: server.id, timestamp: Date.now() }))
  const redirectUri = `${window.location.origin}/components/AITerminal/oauth/callback`
  
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: server.oauth.clientId,
    redirect_uri: redirectUri,
    scope: server.oauth.scopes?.join(' ') || '',
    state: state
  })
  
  const authUrl = `${server.oauth.authorizationUrl}?${params.toString()}`
  
  // Store the server ID in sessionStorage for callback handling
  sessionStorage.setItem('oauth_flow_server_id', server.id)
  
  return authUrl
}

export const handleOAuthCallback = async (code: string, state: string, server: MCPServer): Promise<{ accessToken: string; refreshToken?: string; expiresIn?: number }> => {
  if (!server.oauth?.tokenUrl || !server.oauth?.clientId) {
    throw new Error('OAuth configuration incomplete')
  }
  
  const redirectUri = `${window.location.origin}/components/AITerminal/oauth/callback`
  
  const tokenRequest: Record<string, string> = {
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: server.oauth.clientId,
  }
  
  if (server.oauth.clientSecret) {
    tokenRequest.client_secret = server.oauth.clientSecret
  }
  
  const response = await fetch(server.oauth.tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json',
    },
    body: new URLSearchParams(tokenRequest).toString()
  })
  
  if (!response.ok) {
    const errorData = await response.text()
    throw new Error(`Token exchange failed: ${errorData}`)
  }
  
  const tokenData = await response.json()
  
  return {
    accessToken: tokenData.access_token,
    refreshToken: tokenData.refresh_token,
    expiresIn: tokenData.expires_in
  }
}

interface MCPServerManagerProps {
  mcpServers: MCPServer[]
  setMcpServers: (servers: MCPServer[] | ((prev: MCPServer[]) => MCPServer[])) => void
  fetchMcpTools: (server: MCPServer) => Promise<void>
  mcpTools: Record<string, any[]>
  loadingTools: Record<string, boolean>
  isChatLoading: boolean // To disable buttons when chat is processing
}

const MCPServerManager: React.FC<MCPServerManagerProps> = ({
  mcpServers,
  setMcpServers,
  fetchMcpTools,
  mcpTools,
  loadingTools,
  isChatLoading
}) => {
  const [showMcpModal, setShowMcpModal] = useState(false)
  const [showServerSettingsModal, setShowServerSettingsModal] = useState(false)
  const [selectedServer, setSelectedServer] = useState<MCPServer | null>(null)
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null)
  const [editingConfigMode, setEditingConfigMode] = useState<'simple' | 'json'>('simple')
  const [editingJsonConfig, setEditingJsonConfig] = useState('')
  const [showEnvPasswords, setShowEnvPasswords] = useState<Record<string, boolean>>({})
  const [newMcpServer, setNewMcpServer] = useState<Omit<MCPServer, 'id' | 'enabled'> & { type: 'sse' | 'stdio' }>({ name: '', url: '', type: 'sse', env: {}, command: '', args: [] })
  const [configMode, setConfigMode] = useState<'simple' | 'json'>('simple')
  const [jsonConfig, setJsonConfig] = useState('')

  // Load MCP servers from localStorage on component mount
  useEffect(() => {
    const storedServers = loadMcpServersFromStorage()
    setMcpServers(storedServers) 
  }, [])

  const handleSetMcpServers = useCallback((servers: MCPServer[] | ((prev: MCPServer[]) => MCPServer[])) => {
    console.log('🔍 handleSetMcpServers called with:', typeof servers === 'function' ? 'function' : servers)
    
    setMcpServers(prevServers => {
      console.log('🔍 Previous servers:', prevServers)
      const newServers = typeof servers === 'function' ? servers(prevServers) : servers
      console.log('🔍 New servers calculated:', newServers)
      
      saveMcpServersToStorage(newServers)
      return newServers
    })
  }, [setMcpServers])

  const toggleMcpServerEnabled = async (serverId: string) => {
    const server = mcpServers.find(s => s.id === serverId)
    if (!server) return
    
    const updatedServer = { ...server, enabled: !server.enabled }
    
    handleSetMcpServers(prev => prev.map(s => 
      s.id === serverId ? updatedServer : s
    ))
    
    if (updatedServer.enabled) {
      await fetchMcpTools(updatedServer)
    } else {
      // Optional: Clear tools from state if server is disabled
      // This depends on how mcpTools state is managed in the parent
    }
  }

  const addMcpServer = () => {
    try {
      let newServerToAdd: MCPServer
      
      console.log('🔍 Adding new MCP server, configMode:', configMode)
      
      if (configMode === 'json') {
        const config = JSON.parse(jsonConfig)
        const serverName = Object.keys(config.mcpServers)[0]
        const serverConfig = config.mcpServers[serverName]
        
        newServerToAdd = {
          id: Date.now().toString(),
          name: serverName,
          url: serverConfig.url || '', // Ensure url is present
          type: serverConfig.command ? 'stdio' : 'sse', // Infer type
          enabled: true,
          env: serverConfig.env || {},
          command: serverConfig.command,
          args: serverConfig.args || []
        }
      } else {
        if (!newMcpServer.name || (!newMcpServer.url && newMcpServer.type === 'sse') || (!newMcpServer.command && newMcpServer.type === 'stdio')) {
          alert('Please provide a name and URL/command for the server.')
          return
        }
        
        newServerToAdd = {
          id: Date.now().toString(),
          enabled: true,
          ...newMcpServer,
          url: newMcpServer.type === 'sse' ? newMcpServer.url : '',
          command: newMcpServer.type === 'stdio' ? newMcpServer.command : '',
        }
      }
      
      console.log('🔍 New server to add:', newServerToAdd)
      console.log('🔍 Current mcpServers before adding:', mcpServers)
      
      handleSetMcpServers(prev => {
        const updatedServers = [...prev, newServerToAdd]
        console.log('🔍 Updated servers array:', updatedServers)
        return updatedServers
      })
      
      setNewMcpServer({ name: '', url: '', type: 'sse', env: {}, command: '', args: [] })
      setJsonConfig('')
      setShowMcpModal(false)
      
      console.log('✅ Server added successfully')
    } catch (error) {
      console.error('Error adding MCP server:', error)
      alert('Invalid JSON configuration. Please check your syntax.')
    }
  }

  const isSensitiveEnvVar = (key: string, value: string) => {
    const sensitiveKeys = ['key', 'secret', 'token', 'password', 'auth', 'credential']
    const keyLower = key.toLowerCase()
    return sensitiveKeys.some(sensitive => keyLower.includes(sensitive)) || (typeof value === 'string' && value.length > 20)
  }

  const openServerSettings = (server: MCPServer) => {
    setSelectedServer(server)
    setEditingServer({ ...server })
    setEditingConfigMode('simple')
    setEditingJsonConfig('')
    setShowServerSettingsModal(true)
  }

  const saveServerSettings = () => {
    if (!editingServer) return
    
    try {
      let serverToSave = { ...editingServer } // Create a mutable copy
      
      if (editingConfigMode === 'json' && editingJsonConfig.trim()) {
        const config = JSON.parse(editingJsonConfig)
        const serverName = Object.keys(config.mcpServers)[0]
        const serverConfig = config.mcpServers[serverName]
        
        serverToSave = {
          ...editingServer,
          name: serverName,
          url: serverConfig.url || editingServer.url, // Keep original if not in JSON
          type: serverConfig.command ? 'stdio' : (serverConfig.url ? 'sse' : editingServer.type), // Infer type
          env: serverConfig.env || editingServer.env || {},
          command: serverConfig.command || editingServer.command,
          args: serverConfig.args || editingServer.args || []
        }
      }
      
      handleSetMcpServers(prev => prev.map(server => 
        server.id === serverToSave.id ? serverToSave : server
      ))
      setShowServerSettingsModal(false)
      setSelectedServer(null)
      setEditingServer(null)
      setEditingConfigMode('simple')
      setEditingJsonConfig('')
    } catch (error) {
      console.error('Error saving server settings:', error)
      alert('Invalid JSON configuration. Please check your syntax.')
    }
  }

  const deleteServer = (serverId: string) => {
    if (confirm('Are you sure you want to delete this server? This action cannot be undone.')) {
      handleSetMcpServers(prev => prev.filter(server => server.id !== serverId))
      // Clearing tools for the deleted server should be handled by the parent if mcpTools is managed there
      setShowServerSettingsModal(false)
      setSelectedServer(null)
      setEditingServer(null)
    }
  }

  return (
    <>
      {/* MCP Server Controls Trigger */} 
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-6 px-2 bg-purple-100 hover:bg-purple-200 border-purple-300 text-purple-700"
        onClick={() => setShowMcpModal(true)}
        disabled={isChatLoading} // Disable if chat is loading
      >
        <Plus className="w-3 h-3" />
        MCP Servers
      </Button>

      {/* Trading Server Discovery Button */}
      {/* <Button
        variant="outline"
        size="sm"
        className="text-xs h-6 px-2 bg-blue-100 hover:bg-blue-200 border-blue-300 text-blue-700"
        onClick={() => setShowMcpModal(true)}
        disabled={isChatLoading}
      >
        🔍 Discover Trading Servers
      </Button> */}

      {/* List of Active MCP Servers (optional display here or managed by parent) */} 
      {mcpServers.filter(server => server.enabled).map(server => (
        <Button
          key={server.id}
          variant="outline"
          size="sm"
          className="text-xs h-6 px-2 bg-green-100 hover:bg-green-200 border-green-300 text-green-700"
          onClick={() => openServerSettings(server)} // Open settings for this server
          disabled={isChatLoading}
        >
          <Settings className="w-3 h-3 mr-1" />
          {server.name}
          <X className="w-3 h-3 ml-1" onClick={(e) => { e.stopPropagation(); toggleMcpServerEnabled(server.id); }} />
        </Button>
      ))}

      {/* MCP Server Modal */} 
      {showMcpModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMcpModal(false)
            }
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col text-black">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Manage MCP Servers</h3>
              <Button variant="outline" size="sm" onClick={() => setShowMcpModal(false)} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-3 text-gray-700">Available Servers</h4>
                <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                  {mcpServers.map(server => (
                    <div key={server.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">{server.name}</div>
                          <div className="text-xs text-gray-500 mt-1 break-all">
                            {server.type === 'stdio' && server.command ? (
                              <span className="font-mono">{server.command} {server.args?.join(' ')}</span>
                            ) : (
                              server.url
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Type: {server.type.toUpperCase()}
                            {server.env && Object.keys(server.env).length > 0 && (
                              <span className="ml-2">• Env vars: {Object.keys(server.env).length}</span>
                            )}
                          </div>
                        </div>
                        <div className="ml-3 flex flex-col items-end flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${server.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                              {server.enabled ? 'Enabled' : 'Disabled'}
                            </div>
                            <Button size="sm" variant="outline" onClick={() => openServerSettings(server)} className="h-8 w-8 p-0" title="Server Settings">
                              <Settings className="w-4 h-4" />
                            </Button>
                          </div>
                          <button
                            onClick={() => toggleMcpServerEnabled(server.id)}
                            className={`mt-2 relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${server.enabled ? 'bg-[#28728d]' : 'bg-gray-300'}`}
                            title={server.enabled ? 'Disable server' : 'Enable server'}
                          >
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${server.enabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      </div>
                      {server.env && Object.keys(server.env).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs font-medium text-gray-600 mb-2">Environment Variables:</div>
                          <div className="text-xs text-gray-500 font-mono space-y-1 max-h-20 overflow-y-auto">
                            {Object.entries(server.env).map(([key, value]) => (
                              <div key={key} className="truncate">
                                <span className="text-gray-700">{key}</span>=<span className="text-gray-500">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {server.enabled && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs font-medium text-gray-600 mb-2">Available Tools:</div>
                          {loadingTools[server.id] ? (
                            <div className="text-xs text-gray-500 italic">Loading tools...</div>
                          ) : mcpTools[server.id] && mcpTools[server.id].length > 0 ? (
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {mcpTools[server.id].map((tool: any, index: number) => (
                                <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                                  <div className="font-medium text-gray-700">{tool.name}</div>
                                  {tool.description && (
                                    <div className="text-gray-500 mt-1 line-clamp-4">{tool.description}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : mcpTools[server.id] ? (
                            <div className="text-xs text-gray-500 italic">No tools available</div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => fetchMcpTools(server)} className="text-xs h-6 px-2">
                              Load Tools
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-medium mb-3 text-gray-700">Recommended Trading Servers</h4>
                <div className="grid gap-3 mb-6">
                  {RECOMMENDED_TRADING_SERVERS.map((server, index) => (
                    <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{server.name}</div>
                          <div className="text-xs text-gray-600 mt-1">{server.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{server.category}</span>
                            <span className="ml-2">{server.type.toUpperCase()}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const newServer: MCPServer = {
                              id: Date.now().toString(),
                              name: server.name,
                              url: server.url,
                              type: server.type,
                              enabled: false,
                              env: server.env || {},
                              command: server.command,
                              args: server.args
                            }
                            handleSetMcpServers(prev => [...prev, newServer])
                          }}
                          className="text-xs h-7 px-2"
                          disabled={isChatLoading}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <h4 className="text-sm font-medium mb-3 text-gray-700 border-t border-gray-200 pt-6">Add Custom Server</h4>
                <div className="flex gap-2 mb-4">
                  <Button size="sm" variant={configMode === 'simple' ? 'default' : 'outline'} onClick={() => setConfigMode('simple')} className="flex-1">
                    Simple Config
                  </Button>
                  <Button size="sm" variant={configMode === 'json' ? 'default' : 'outline'} onClick={() => setConfigMode('json')} className="flex-1">
                    Raw Config (JSON)
                  </Button>
                </div>
                {configMode === 'simple' ? (
                  <div className="space-y-3">
                    <Input placeholder="Server name" value={newMcpServer.name} onChange={(e) => setNewMcpServer(prev => ({ ...prev, name: e.target.value }))} className="text-sm" />
                    <select value={newMcpServer.type} onChange={(e) => setNewMcpServer(prev => ({ ...prev, type: e.target.value as 'sse' | 'stdio' }))} className="w-full p-2 border rounded text-sm bg-white">
                      <option value="sse">SSE (Server-Sent Events)</option>
                      <option value="stdio">STDIO (Local Process)</option>
                    </select>
                    {newMcpServer.type === 'sse' && (
                      <Input placeholder="Server URL (e.g., http://localhost:8765/sse)" value={newMcpServer.url} onChange={(e) => setNewMcpServer(prev => ({ ...prev, url: e.target.value }))} className="text-sm" />
                    )}
                    {newMcpServer.type === 'stdio' && (
                      <Input placeholder="Command (e.g., npx -y @mcp-server/example)" value={newMcpServer.command} onChange={(e) => setNewMcpServer(prev => ({ ...prev, command: e.target.value }))} className="text-sm" />
                    )}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Arguments (for STDIO, optional)</label>
                      <Input placeholder="arg1 arg2" value={newMcpServer.args?.join(' ') || ''} onChange={(e) => setNewMcpServer(prev => ({ ...prev, args: e.target.value.split(' ').filter(Boolean) }))} className="text-sm" disabled={newMcpServer.type !== 'stdio'} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Environment Variables (optional)</label>
                      <textarea placeholder="KEY1=value1\nKEY2=value2" className="w-full p-2 border rounded text-xs h-20 resize-none" onChange={(e) => {
                         const envVars: Record<string, string> = {}
                         e.target.value.split('\n').forEach(line => {
                           const [key, ...valueParts] = line.split('=')
                           if (key && valueParts.length > 0) {
                             envVars[key.trim()] = valueParts.join('=').trim()
                           }
                         })
                         setNewMcpServer(prev => ({ ...prev, env: envVars }))
                       }} />
                    </div>
                    <Button onClick={addMcpServer} disabled={!newMcpServer.name || (newMcpServer.type === 'sse' && !newMcpServer.url) || (newMcpServer.type === 'stdio' && !newMcpServer.command) || isChatLoading} className="w-full" size="sm">
                      Add Server
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-xs text-gray-600 mb-2">Please copy the configuration JSON from the MCP server's documentation:</div>
                    <textarea placeholder={`{\n  "mcpServers": {\n    "example-server": {\n      "command": "npx",\n      "args": ["-y", "@mcp-server-example"],\n      "env": {\n        "API_KEY": "your-api-key"\n      }\n    }\n  }\n}`} value={jsonConfig} onChange={(e) => setJsonConfig(e.target.value)} className="w-full p-3 border rounded text-xs h-40 resize-none font-mono" />
                    <Button onClick={addMcpServer} disabled={!jsonConfig.trim() || isChatLoading} className="w-full" size="sm">
                      Confirm & Add Server
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Server Settings Modal */} 
      {showServerSettingsModal && selectedServer && editingServer && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowServerSettingsModal(false)
            }
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col text-black">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Server Settings: {selectedServer.name}</h3>
              <Button variant="outline" size="sm" onClick={() => setShowServerSettingsModal(false)} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {editingServer.env && Object.keys(editingServer.env).length > 0 && Object.entries(editingServer.env).some(([key, value]) => isSensitiveEnvVar(key, value as string)) && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="text-yellow-600 text-sm">⚠️</div>
                    <div className="text-xs text-yellow-800">
                      <strong>Security Notice:</strong> This server contains sensitive environment variables. Data is stored with basic obfuscation in your browser's localStorage.
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                <div className="flex gap-2 mb-4">
                  <Button size="sm" variant={editingConfigMode === 'simple' ? 'default' : 'outline'} onClick={() => setEditingConfigMode('simple')} className="flex-1">
                    Simple Config
                  </Button>
                  <Button size="sm" variant={editingConfigMode === 'json' ? 'default' : 'outline'} onClick={() => setEditingConfigMode('json')} className="flex-1">
                    Raw Config (JSON)
                  </Button>
                </div>
                {editingConfigMode === 'simple' ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Server Name</label>
                      <Input value={editingServer.name} onChange={(e) => setEditingServer(prev => prev ? { ...prev, name: e.target.value } : null)} className="text-sm" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Type</label>
                      <select value={editingServer.type} onChange={(e) => setEditingServer(prev => prev ? { ...prev, type: e.target.value as 'sse' | 'stdio' } : null)} className="w-full p-2 border rounded text-sm bg-white">
                        <option value="sse">SSE (Server-Sent Events)</option>
                        <option value="stdio">STDIO (Local Process)</option>
                      </select>
                    </div>
                    {editingServer.type === 'sse' && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">URL</label>
                        <Input value={editingServer.url} onChange={(e) => setEditingServer(prev => prev ? { ...prev, url: e.target.value } : null)} className="text-sm" />
                      </div>
                    )}
                    {editingServer.type === 'stdio' && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Command</label>
                        <Input value={editingServer.command || ''} onChange={(e) => setEditingServer(prev => prev ? { ...prev, command: e.target.value } : null)} className="text-sm" />
                      </div>
                    )}
                    {editingServer.type === 'stdio' && (
                       <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Arguments</label>
                        <Input value={editingServer.args?.join(' ') || ''} onChange={(e) => setEditingServer(prev => prev ? { ...prev, args: e.target.value.split(' ').filter(Boolean) } : null)} placeholder="arg1 arg2 arg3" className="text-sm" />
                      </div>
                    )}
                    {editingServer.env && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Environment Variables</label>
                        <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-3">
                          {Object.entries(editingServer.env).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="text-xs font-medium text-gray-600">{key}</div>
                                <div className="flex items-center gap-2">
                                  <Input type={isSensitiveEnvVar(key, value as string) && !showEnvPasswords[key] ? 'password' : 'text'} value={value as string} onChange={(e) => setEditingServer(prev => prev ? { ...prev, env: { ...prev.env, [key]: e.target.value } } : null)} className="text-xs h-7" />
                                  {isSensitiveEnvVar(key, value as string) && (
                                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowEnvPasswords(prev => ({ ...prev, [key]: !prev[key] }))}>
                                      {showEnvPasswords[key] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={() => setEditingServer(prev => {
                                if (!prev || !prev.env) return null
                                const newEnv = { ...prev.env }
                                delete newEnv[key]
                                return { ...prev, env: newEnv }
                              })}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Raw JSON Configuration</label>
                      <div className="text-xs text-gray-600 mb-2">Edit server configuration. Changes apply on save.</div>
                      <textarea value={editingJsonConfig || JSON.stringify({ mcpServers: { [editingServer.name]: {
                        command: editingServer.command || undefined,
                        url: editingServer.url || undefined,
                        args: editingServer.args || undefined,
                        env: editingServer.env || undefined
                      }}}, null, 2)} onChange={(e) => setEditingJsonConfig(e.target.value)} className="w-full p-3 border rounded text-xs h-60 resize-none font-mono" />
                      <div className="text-xs text-gray-500 mt-1">⚠️ Ensure server name is the key in "mcpServers".</div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between p-6 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <Button variant={editingServer.enabled ? 'default' : 'outline'} size="sm" onClick={() => setEditingServer(prev => prev ? { ...prev, enabled: !prev.enabled } : null)} disabled={isChatLoading}>
                  {editingServer.enabled ? 'Enabled' : 'Disabled'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => deleteServer(selectedServer.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" disabled={isChatLoading}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setShowServerSettingsModal(false)} size="sm" disabled={isChatLoading}>Cancel</Button>
                <Button onClick={saveServerSettings} size="sm" disabled={isChatLoading}>Save Changes</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MCPServerManager 